/**
 * Client-side cancellation policy utilities
 * Mirrors the logic from CancellationPolicyService on the backend
 */

export interface CancellationRuleClient {
  hoursBeforeBooking: number;
  refundPercentage: number;
}

/**
 * Default cancellation policy rules — matches DEFAULT_POLICY in cancellation-policy.service.ts
 */
export const DEFAULT_POLICY_RULES: CancellationRuleClient[] = [
  { hoursBeforeBooking: 168, refundPercentage: 100 }, // 7+ days
  { hoursBeforeBooking: 72, refundPercentage: 75 }, // 3-7 days
  { hoursBeforeBooking: 24, refundPercentage: 50 }, // 1-3 days
  { hoursBeforeBooking: 0, refundPercentage: 0 }, // <24 hours
];

/**
 * Returns true if a venue has a custom cancellation policy configured
 */
export function isPolicyConfigured(cancellationPolicy: unknown): boolean {
  if (!cancellationPolicy || typeof cancellationPolicy !== 'object') return false;
  const policy = cancellationPolicy as { rules?: unknown };
  return Array.isArray(policy.rules) && policy.rules.length > 0;
}

/**
 * Extracts rules from a venue's cancellationPolicy JSON, falling back to DEFAULT_POLICY_RULES
 */
export function getPolicyRules(cancellationPolicy: unknown): CancellationRuleClient[] {
  if (!isPolicyConfigured(cancellationPolicy)) return DEFAULT_POLICY_RULES;
  const policy = cancellationPolicy as { rules: CancellationRuleClient[] };
  return [...policy.rules].sort((a, b) => b.hoursBeforeBooking - a.hoursBeforeBooking);
}

/**
 * Formats cancellation rules into human-readable bullet points.
 *
 * Rules are sorted descending by hoursBeforeBooking. For rule[i], the effective
 * window is [rule[i].hours, rule[i-1].hours) — so rule[i-1] (the higher threshold)
 * provides the upper bound and rule[i] provides the lower bound.
 */
export function formatPolicySummary(rules: CancellationRuleClient[]): string[] {
  const sorted = [...rules].sort((a, b) => b.hoursBeforeBooking - a.hoursBeforeBooking);
  const summary: string[] = [];

  const formatDuration = (hours: number): string => {
    if (hours < 72) return `${hours} hours`;
    return `${Math.floor(hours / 24)} days`;
  };

  for (let i = 0; i < sorted.length; i++) {
    const rule = sorted[i];
    const prevRule = sorted[i - 1]; // rule with the HIGHER threshold (upper bound)
    const refundText =
      rule.refundPercentage === 0 ? 'No refund' : `${rule.refundPercentage}% refund`;

    if (i === 0) {
      // Highest threshold: applies when cancellation is well in advance
      summary.push(`More than ${formatDuration(rule.hoursBeforeBooking)} before: ${refundText}`);
    } else if (rule.hoursBeforeBooking === 0) {
      // Catch-all: applies when less than prevRule.hours remain
      summary.push(`Less than ${formatDuration(prevRule.hoursBeforeBooking)} before: ${refundText}`);
    } else {
      // Middle rule: applies in the window [rule.hours, prevRule.hours)
      summary.push(
        `${formatDuration(rule.hoursBeforeBooking)}–${formatDuration(prevRule.hoursBeforeBooking)} before: ${refundText}`
      );
    }
  }

  return summary;
}
